// Google Ads metric formatting — PURE + tested.
//
// One formatter shared by the KPI tiles, the tree table, the campaign-type split
// and the CSV, so a metric can never read one way in a tile and another in a
// column.
//
// ⚠️ THE EM DASH IS LOAD-BEARING. `null` means Google did not report the metric
// for this scope, and it renders "—". It must never fall back to "0" or "0%":
// Google writes 0.0 rather than NULL where a share does not apply (170 live
// campaign-days report 0% click share while carrying 1,719 clicks), and reporting
// an unmeasured impression share as 0% tells a client they lost the whole auction
// when we simply cannot see it.

import type { CurrencyAmount, GoogleAdsMetricKey, GoogleAdsMetrics } from '../../types/googleAdsReporting';
import { formatReportingAmount } from '../../lib/reportingMoney';
import { isMoneyMetric, metricDef, metricMoney, metricScalar } from '../../lib/googleAdsMetrics';

/** The single "not measured" marker. */
export const NOT_MEASURED = '—';

/** Compact integers: 1234567 → "1,234,567". */
export function formatCount(n: number): string {
  return Math.round(n).toLocaleString('en-US');
}

/** Fractional counts (conversions) keep 2dp only when they actually have one. */
export function formatDecimal(n: number): string {
  const rounded = Math.round(n * 100) / 100;
  return Number.isInteger(rounded)
    ? rounded.toLocaleString('en-US')
    : rounded.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function formatPct(n: number): string {
  return `${(Math.round(n * 100) / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}%`;
}

/** A per-currency money stat: "A$12,340.50 · NZ$6,780.00". Empty → "—". */
export function formatMoneyAmounts(amounts: CurrencyAmount[]): string {
  if (amounts.length === 0) return NOT_MEASURED;
  return [...amounts]
    .sort((a, b) => a.currency.localeCompare(b.currency))
    .map((a) => formatReportingAmount(a.amount, a.currency))
    .join(' · ');
}

/** ROAS and other multiples: "2.50x" per currency (never a money symbol). */
export function formatMultiple(amounts: CurrencyAmount[]): string {
  if (amounts.length === 0) return NOT_MEASURED;
  return [...amounts]
    .sort((a, b) => a.currency.localeCompare(b.currency))
    .map((a) => `${(Math.round(a.amount * 100) / 100).toFixed(2)}x`)
    .join(' · ');
}

/**
 * Render any metric off a metrics block. The ONE place a metric becomes text.
 *
 * A money metric with no currencies and a scalar metric that is null both render
 * "—" — in each case the honest statement is "we have no measurement here".
 */
export function formatMetric(metrics: GoogleAdsMetrics, key: GoogleAdsMetricKey): string {
  const def = metricDef(key);
  if (!def) return NOT_MEASURED;

  if (def.kind === 'multiple') return formatMultiple(metricMoney(metrics, key));
  if (isMoneyMetric(key)) return formatMoneyAmounts(metricMoney(metrics, key));

  const value = metricScalar(metrics, key);
  if (value === null) return NOT_MEASURED;
  switch (def.kind) {
    case 'pct':
      return formatPct(value);
    case 'decimal':
      return formatDecimal(value);
    default:
      return formatCount(value);
  }
}

/** True when a metric has no measurement in this scope (drives muted styling). */
export function isMetricUnmeasured(metrics: GoogleAdsMetrics, key: GoogleAdsMetricKey): boolean {
  if (isMoneyMetric(key)) return metricMoney(metrics, key).length === 0;
  return metricScalar(metrics, key) === null;
}

/**
 * Google's enum values are SCREAMING_SNAKE. Rendered as Title Case, with the
 * campaign types people actually say kept as their real names — "PERFORMANCE_MAX"
 * reading as "Performance Max" is right, but "Pmax" would be wrong and "Performance
 * Max" is what the Google UI shows.
 */
const CHANNEL_LABELS: Record<string, string> = {
  PERFORMANCE_MAX: 'Performance Max',
  SEARCH: 'Search',
  SHOPPING: 'Shopping',
  DISPLAY: 'Display',
  VIDEO: 'Video',
  DEMAND_GEN: 'Demand Gen',
  DISCOVERY: 'Discovery',
  LOCAL: 'Local',
  LOCAL_SERVICES: 'Local Services',
  SMART: 'Smart',
  HOTEL: 'Hotel',
  MULTI_CHANNEL: 'Multi-channel',
  TRAVEL: 'Travel',
};

export function formatEnum(value: string | null | undefined): string {
  if (!value || value === 'UNSPECIFIED' || value === 'UNKNOWN') return NOT_MEASURED;
  const mapped = CHANNEL_LABELS[value];
  if (mapped) return mapped;
  return value
    .toLowerCase()
    .split('_')
    .map((w) => (w.length > 0 ? w[0]!.toUpperCase() + w.slice(1) : w))
    .join(' ');
}

/** Delivery-status colour role, mirroring the Meta breakdown's status dots. */
export type StatusRole = 'active' | 'paused' | 'removed' | 'unknown';

export function googleStatusRole(status: string | null | undefined): StatusRole {
  const s = (status ?? '').toUpperCase();
  if (s === 'ENABLED') return 'active';
  if (s === 'PAUSED') return 'paused';
  if (s === 'REMOVED') return 'removed';
  return 'unknown';
}

/** A budget with its period tag: "$16.66/day". */
export function formatBudget(
  amount: CurrencyAmount | null,
  period: string | null | undefined,
): string {
  if (!amount) return NOT_MEASURED;
  const tag = (period ?? '').toUpperCase() === 'MONTHLY' ? '/mo' : '/day';
  return `${formatReportingAmount(amount.amount, amount.currency)}${tag}`;
}

/** Target ROAS is stored as a ratio (4 = 400%); shown the way Google shows it. */
export function formatTargetRoas(value: number | null): string {
  if (value == null) return NOT_MEASURED;
  return `${(Math.round(value * 10000) / 100).toLocaleString('en-US')}%`;
}
